import React, { useEffect, useRef, useState, useMemo } from 'react'
import Container from '@mui/material/Container'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import MicOffIcon from '@mui/icons-material/MicOff'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTts } from '../contexts/TtsContext'
import { useAuth, useAlert } from '../App'
import { deleteVoice } from '../services/ttsApi'
import { TtsVoice, voiceMatchesLanguage } from '../types/tts'
import VoiceCard from '../components/voices/VoiceCard'
import VoiceFilters from '../components/voices/VoiceFilters'
import { VoiceFilterState } from '../components/voices/VoiceFilters'
import VoiceCloneDialog from '../components/voices/VoiceCloneDialog'
import EmptyState from '../components/EmptyState'
import { usePageTitle } from '../hooks/usePageMeta'
import { useTtsLanguages } from '../hooks/useTtsLanguages'
import { useAudioPreview } from '../hooks/useAudioPreview'
import { tokens } from '../theme/tokens'

const VoiceGallery: React.FC = () => {
  const { t } = useTranslation()
  usePageTitle('Voices')
  const navigate = useNavigate()
  const auth = useAuth() as any
  const { setAlert } = useAlert() as any
  const {
    voices,
    voicesLoading,
    voicesError,
    loadVoices,
    refreshVoices,
    checkTtsHealth,
    ttsHealthy,
  } = useTts()

  const [filters, setFilters] = useState<VoiceFilterState>({
    search: '',
    language: '',
  })
  const languageInitializedRef = useRef(false)

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false)
  const [deleteDialogVoice, setDeleteDialogVoice] = useState<TtsVoice | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Shared preview state machine (single clip, 200ms fade-out, self-clearing
  // highlight). No resetKey — the gallery is one flat list, so it only stops on
  // unmount.
  const preview = useAudioPreview()

  // Stock voices feed the shared language plumbing (merge + orphan bucket +
  // has-voices-first default).
  const stockVoicesAll = useMemo(() => voices.filter((v) => !v.isCloned), [voices])
  const { languageOptions, languagesLoading, preferredLanguage } = useTtsLanguages(stockVoicesAll)

  // On mount: health check + load voices
  useEffect(() => {
    checkTtsHealth()
    const loadData = async () => {
      try {
        const token = await auth.getToken()
        loadVoices(token)
      } catch {
        // auth error handled elsewhere
      }
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Default the stock language to one that actually has voices (the shared hook
  // ranks premium-with-voices first, then any with voices, then premium/empty).
  useEffect(() => {
    if (languageInitializedRef.current) return
    if (voices.length === 0 || !preferredLanguage) return
    setFilters((f) => (f.language ? f : { ...f, language: preferredLanguage }))
    languageInitializedRef.current = true
  }, [voices, preferredLanguage])

  // Stock voices for the chosen language. A name search OVERRIDES the language
  // narrowing so typing a voice's name finds it regardless of which language chip
  // is active; without a search we narrow to the selected language (orphan voices
  // whose language is unresolved live under the synthetic 'unknown' chip).
  const stockVoices = useMemo(() => {
    const q = filters.search.trim().toLowerCase()
    return voices.filter((v) => {
      if (v.isCloned) return false
      if (q) {
        // Match the visible name (displayName), falling back to the raw name.
        return v.displayName.toLowerCase().includes(q) || v.name.toLowerCase().includes(q)
      }
      // A voice advertising several languages belongs under EVERY one of them.
      if (filters.language && !voiceMatchesLanguage(v, filters.language)) return false
      return true
    })
  }, [voices, filters])

  // Cloned voices ("My Voices"), narrowed by the same name search.
  const clonedVoices = useMemo(() => {
    return voices.filter((v) => {
      if (!v.isCloned) return false
      if (filters.search) {
        const q = filters.search.toLowerCase()
        // Search the visible name (displayName), falling back to the raw name.
        if (!v.displayName.toLowerCase().includes(q) && !v.name.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [voices, filters])

  const handleSelect = (voiceId: string) => {
    navigate(`/speech/tts?voice=${encodeURIComponent(voiceId)}`)
  }

  const handleDeleteRequest = (voiceId: string) => {
    const voice = voices.find((v) => v.voice_id === voiceId)
    if (voice) setDeleteDialogVoice(voice)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteDialogVoice) return
    setDeleting(true)
    try {
      const token = await auth.getToken()
      await deleteVoice(deleteDialogVoice.voice_id, token)
      setAlert({ type: 'success', text: t('voiceDeleted', 'Voice deleted successfully') })
      setDeleteDialogVoice(null)
      await refreshVoices(token)
    } catch (err: any) {
      setAlert({ type: 'error', text: err.message || t('deleteError', 'Failed to delete voice') })
    } finally {
      setDeleting(false)
    }
  }

  const handleCloneSuccess = async () => {
    try {
      const token = await auth.getToken()
      await refreshVoices(token)
    } catch {
      // ignore
    }
  }

  const handleOpenCloneDialog = () => setCloneDialogOpen(true)

  return (
    <Fade in timeout={300}>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('voiceGallery', 'Voice Gallery')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('voiceGallerySubtitle', 'Browse stock voices or manage your cloned voices')}
        </Typography>
        {ttsHealthy === false && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('ttsUnavailable', 'TTS service is currently unavailable')}
          </Alert>
        )}
      </Box>

      {/* Language-first stock browsing + name search */}
      <VoiceFilters
        filters={filters}
        onFilterChange={setFilters}
        languages={languageOptions}
        languagesLoading={languagesLoading && languageOptions.length === 0}
      />

      {/* Loading state */}
      {voicesLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} role="status" aria-label={t('loading', 'Loading')}>
          <CircularProgress sx={{ color: tokens.colors.primary.darkText }} aria-hidden="true" />
        </Box>
      )}

      {/* Error state */}
      {voicesError && !voicesLoading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {voicesError}
        </Alert>
      )}

      {/* Stock Voices */}
      {!voicesLoading && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {t('stockVoices', 'Stock Voices')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('stockVoicesSubtitle', 'Pre-built voices ready to use')}
          </Typography>

          {stockVoices.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t('noStockVoices', 'No stock voices found')}
            </Typography>
          ) : (
            <Grid container spacing={2} alignItems="stretch">
              {stockVoices.map((voice) => (
                <Grid item xs={12} sm={6} md={3} key={voice.voice_id} sx={{ height: 260 }}>
                  <VoiceCard
                    voice={voice}
                    onSelect={handleSelect}
                    onPreview={preview.toggle}
                    isPlaying={preview.playingId === voice.voice_id}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {!voicesLoading && <Divider sx={{ mb: 5 }} />}

      {/* Cloned Voices -- separate "My Voices" space (zero-shot × any language) */}
      {!voicesLoading && (
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {t('myClonedVoices', 'My Cloned Voices')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('clonedVoicesSubtitle', 'Voices you have created from your own recordings')}
          </Typography>

          {/* M21: Empty state for cloned voices section */}
          {clonedVoices.length === 0 ? (
            <EmptyState
              icon={<MicOffIcon sx={{ fontSize: 64 }} />}
              title={t('voices.noClonedVoices', 'No cloned voices yet')}
              description={t('voices.clonePrompt', 'Clone a voice to create a custom voice for your TTS applications.')}
              action={{ label: t('voices.cloneNew', '+ Clone New Voice'), onClick: handleOpenCloneDialog }}
            />
          ) : (
            <Grid container spacing={2} alignItems="stretch">
              {clonedVoices.map((voice) => (
                <Grid item xs={12} sm={6} md={3} key={voice.voice_id} sx={{ height: 260 }}>
                  <VoiceCard
                    voice={voice}
                    onSelect={handleSelect}
                    onDelete={handleDeleteRequest}
                    onPreview={preview.toggle}
                    isPlaying={preview.playingId === voice.voice_id}
                  />
                </Grid>
              ))}

              {/* Add New Voice card */}
              <Grid item xs={12} sm={6} md={3} sx={{ height: 260 }}>
                <Card
                  onClick={handleOpenCloneDialog}
                  role="button"
                  tabIndex={0}
                  aria-label={t('addNewVoice', 'Add New Voice')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenCloneDialog() } }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'rgba(0,125,138,0.4)',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: tokens.colors.primary.darkText,
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AddIcon sx={{ fontSize: 40, color: tokens.colors.primary.darkText, mb: 1 }} />
                    <Typography variant="body1" fontWeight={700} color={tokens.colors.primary.darkText}>
                      {t('addNewVoice', 'Add New Voice')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('cloneFromRecordings', 'Clone from your recordings')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Clone Dialog */}
      <VoiceCloneDialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        onSuccess={handleCloneSuccess}
      />

      {/* Delete Confirmation Dialog — M20: i18n with voice name interpolation */}
      <Dialog
        open={Boolean(deleteDialogVoice)}
        onClose={() => !deleting && setDeleteDialogVoice(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-voice-dialog-title"
      >
        <DialogTitle id="delete-voice-dialog-title">{t('deleteVoice', 'Delete Voice')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('voices.deleteConfirm', {
              defaultValue: 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
              name: deleteDialogVoice?.name ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogVoice(null)}
            disabled={deleting}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? t('deleting', 'Deleting...') : t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Fade>
  )
}

export default VoiceGallery
